/**
 * Trees and rocks on a battlefield — things to fight *around*.
 *
 * The list itself is state and lives on `BattleState.obstacles`: it is rolled
 * from the battle's own seed at `createBattle`, hashed inside the battle block
 * of `hashWorld`, and written into the save. This file is the machinery on top
 * of it, and it is three things:
 *
 *   **The occupancy bitmap.** `World.obstacleGrid`, one byte a tile, derived
 *   from the list and therefore deliberately unhashed and unsaved — exactly like
 *   the flow fields and the spatial hash. It is rebuilt by `rebuildObstacleGrid`
 *   whenever the list changes and once on load, and it is `null` in every world
 *   that has no scatter, which is every settlement in the game. That null is the
 *   whole performance story: the two hot readers (`tileBlocks` in the collision
 *   pass, and the Dijkstra in `flow.ts`) pay one null check per call in a siege
 *   and one array read per call on a field.
 *
 *   **Line of sight.** `lineBlocked` walks the grid with an integer Bresenham,
 *   asked once per missile swing rather than once per candidate target — a
 *   raycast per candidate in the targeting sweep would be the most expensive
 *   thing in the tick by a wide margin, and being one swing late to notice a
 *   tree is invisible.
 *
 *   **Cover.** A model standing under a canopy takes `OBSTACLE_COVER` less from
 *   arrows. Applied at `damageUnit`, which is the one door every point of damage
 *   in the game comes through.
 *
 * Why not a fourth map layer: a layer is `MAP_TILES` bytes in the hash and in
 * every save, forever, for a feature that exists on a battlefield and nowhere
 * else — and adding it would change the digest of every settlement world that
 * has never seen a tree. A list of forty circles is the state; the grid is the
 * cache.
 */

import { MAP_H, MAP_W, MAP_TILES } from './constants.ts';
import { OB_COVER, OB_SOLID, ObstacleKind, Terrain, type FieldObstacle } from './types.ts';
import { nextFloat, nextInt, type Rng } from '../core/rng.ts';
import type { World } from './world.ts';

/** How many pieces of scatter a field carries. Dense enough to matter, sparse enough to march through. */
const SCATTER_MIN = 26;
const SCATTER_MAX = 40;
/** Nothing is scattered inside this margin, so nobody musters into a boulder. */
const EDGE_MARGIN = 10;

/**
 * Roll a field's scatter.
 *
 * Pure in `rng` and nothing else, so the same seed lays the same wood down every
 * time — which is the property the whole thing rests on. Two obstacles are
 * allowed to overlap; a copse with a rock in it is a better fight than a grid of
 * tidy discs, and the bitmap ors them together anyway.
 *
 * `keepClear` is the two deployment bands. A rock that lands inside a company's
 * muster point would swallow half of it, and while `evictObstacles` below would
 * shove those models out, being shoved out of your own formation before the
 * horns is not a battle, it is a bug that happens to be deterministic.
 */
export function scatterObstacles(
  rng: Rng,
  keepClear: ReadonlyArray<{ x0: number; y0: number; x1: number; y1: number }> = [],
): FieldObstacle[] {
  const out: FieldObstacle[] = [];
  const want = nextInt(rng, SCATTER_MIN, SCATTER_MAX + 1);
  let guard = 0;
  while (out.length < want && guard++ < want * 8) {
    const x = nextInt(rng, EDGE_MARGIN, MAP_W - EDGE_MARGIN) + 0.5;
    const y = nextInt(rng, EDGE_MARGIN, MAP_H - EDGE_MARGIN) + 0.5;
    const roll = nextFloat(rng);
    const kind = roll < 0.5 ? ObstacleKind.Tree : roll < 0.78 ? ObstacleKind.Thicket : ObstacleKind.Rock;
    const r = kind === ObstacleKind.Rock ? 1.5 + nextFloat(rng) * 2.2 : 2 + nextFloat(rng) * 3.4;
    let clear = true;
    for (const z of keepClear) {
      if (x + r >= z.x0 && x - r <= z.x1 && y + r >= z.y0 && y - r <= z.y1) {
        clear = false;
        break;
      }
    }
    if (!clear) continue;
    out.push({ kind, x, y, r });
  }
  return out;
}

/**
 * Rebuild the occupancy bitmap from the list.
 *
 * Called from `createBattle`, from the save loader, and from anything that ever
 * changes the list. Allocates only when there is something to hold, and drops the
 * buffer entirely when there is not, so a settlement world never carries 147 KB
 * it has no use for.
 */
export function rebuildObstacleGrid(w: World): void {
  const list = w.battle?.obstacles;
  if (!list || list.length === 0) {
    w.obstacleGrid = null;
    return;
  }
  const g = w.obstacleGrid && w.obstacleGrid.length === MAP_TILES ? w.obstacleGrid : new Uint8Array(MAP_TILES);
  g.fill(0);
  for (const o of list) {
    const solidR = solidRadius(o);
    const r = Math.max(o.r, solidR);
    const x0 = Math.max(0, Math.floor(o.x - r));
    const x1 = Math.min(MAP_W - 1, Math.ceil(o.x + r));
    const y0 = Math.max(0, Math.floor(o.y - r));
    const y1 = Math.min(MAP_H - 1, Math.ceil(o.y + r));
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const dx = tx + 0.5 - o.x;
        const dy = ty + 0.5 - o.y;
        const d2 = dx * dx + dy * dy;
        const k = ty * MAP_W + tx;
        if (d2 <= o.r * o.r) g[k] |= OB_COVER;
        if (d2 <= solidR * solidR) g[k] |= OB_SOLID;
      }
    }
  }
  w.obstacleGrid = g;
}

/**
 * How much of an obstacle refuses feet.
 *
 * A rock is solid all the way across — that is what makes it an anchor for a
 * flank. A tree is a trunk in the middle of a canopy: you can stand in the wood,
 * you cannot stand in the tree. A thicket is all canopy and no trunk, which is
 * what makes it the interesting one — cover you can actually hold.
 */
function solidRadius(o: FieldObstacle): number {
  switch (o.kind) {
    case ObstacleKind.Rock:
      return o.r;
    case ObstacleKind.Tree:
      return 0.7;
    default:
      return 0;
  }
}

/** The grid byte at a tile, or 0 if this world has no scatter. */
export function obstacleAt(w: World, tx: number, ty: number): number {
  const g = w.obstacleGrid;
  if (g === null) return 0;
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return 0;
  return g[ty * MAP_W + tx];
}

/** Is this model standing in cover? Read by the missile half of `damageUnit`. */
export function inCover(w: World, x: number, y: number): boolean {
  return (obstacleAt(w, Math.floor(x), Math.floor(y)) & OB_COVER) !== 0;
}

/**
 * Is the straight line between these two points broken by something solid?
 *
 * Integer Bresenham over the bitmap, with the two endpoint tiles exempt — a
 * bowman standing against a trunk can still shoot past it, and a target standing
 * against one can still be hit. Only a rock *between* them stops the shot, which
 * is the rule a player can see working.
 */
export function lineBlocked(w: World, x0: number, y0: number, x1: number, y1: number): boolean {
  const g = w.obstacleGrid;
  if (g === null) return false;
  let cx = Math.floor(x0);
  let cy = Math.floor(y0);
  const ex = Math.floor(x1);
  const ey = Math.floor(y1);
  if (cx === ex && cy === ey) return false;
  const dx = Math.abs(ex - cx);
  const dy = Math.abs(ey - cy);
  const sx = cx < ex ? 1 : -1;
  const sy = cy < ey ? 1 : -1;
  let err = dx - dy;
  // Bounded so a stray coordinate cannot spin: no line on this map is longer
  // than the diagonal, and a step is at least one tile.
  let guard = MAP_W + MAP_H;
  for (;;) {
    const e2 = err * 2;
    if (e2 > -dy) {
      err -= dy;
      cx += sx;
    }
    if (e2 < dx) {
      err += dx;
      cy += sy;
    }
    if ((cx === ex && cy === ey) || guard-- <= 0) return false;
    if (cx < 0 || cy < 0 || cx >= MAP_W || cy >= MAP_H) return false;
    if ((g[cy * MAP_W + cx] & OB_SOLID) !== 0) return true;
  }
}

/**
 * Paint the ground under the scatter so it reads as a wood rather than as an
 * invisible rule.
 *
 * Terrain is not what blocks anything — the bitmap is — but a canopy over ash
 * plain would be a stand of trees the movement cost had never heard of, and
 * `Terrain.Forest` already costs 1.45 to cross. So the two agree: the wood you
 * can see is the wood you walk slowly through and the wood you cannot be shot
 * cleanly in. Rocks get `StoneOutcrop`, which is what an outcrop is.
 */
export function paintObstacleTerrain(w: World, list: readonly FieldObstacle[]): void {
  const map = w.map;
  for (const o of list) {
    const t = o.kind === ObstacleKind.Rock ? Terrain.StoneOutcrop : Terrain.Forest;
    const x0 = Math.max(0, Math.floor(o.x - o.r));
    const x1 = Math.min(MAP_W - 1, Math.ceil(o.x + o.r));
    const y0 = Math.max(0, Math.floor(o.y - o.r));
    const y1 = Math.min(MAP_H - 1, Math.ceil(o.y + o.r));
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const dx = tx + 0.5 - o.x;
        const dy = ty + 0.5 - o.y;
        if (dx * dx + dy * dy > o.r * o.r) continue;
        map.terrain[map.idx(tx, ty)] = t;
      }
    }
  }
  map.terrainVersion++;
}

/**
 * What the renderer needs and nothing more.
 *
 * The simulation draws nothing. This is the reader: a flat copy of the list, so
 * the scene can put a tree where the fight says there is one, and so that
 * nothing outside `applyCommand` can reach in and move a boulder.
 */
export function fieldObstacles(w: World): FieldObstacle[] {
  const list = w.battle?.obstacles;
  return list ? list.map((o) => ({ ...o })) : [];
}
